/**
 * feAI – Frontend
 * src/components/auth/ProfilePicker.tsx
 *
 * Step 2 (conditional) – Multi-Account profile picker.
 * Shown when the API responds with status: 'MULTI_ACCOUNT'.
 *
 * Renders an elegant grid of profile cards. Each card shows:
 *   • Avatar with initials
 *   • Nome + Cognome
 *   • Username legacy (monospace)
 *   • Role badge
 *
 * Clicking a card calls onSelect(userId) to finalise the login.
 */

import React, { useState } from 'react';
import type { ProfileCard } from '../../types/auth';

interface ProfilePickerProps {
  profiles: ProfileCard[];
  onSelect:  (userId: string) => void;
  onBack:    () => void;
  isLoading: boolean;
  error:     string | null;
}

function initials(first: string, last: string): string {
  return `${first.charAt(0)}${last.charAt(0)}`.toUpperCase();
}

function roleLabel(role: string): string {
  const map: Record<string, string> = {
    agent:  'Agente',
    admin:  'Admin',
    viewer: 'Visualizzatore',
    manager:'Manager',
  };
  return map[role.toLowerCase()] ?? role;
}

// Stagger animation delay by index
const CARD_DELAY_MS = 55;

export const ProfilePicker: React.FC<ProfilePickerProps> = ({
  profiles,
  onSelect,
  onBack,
  isLoading,
  error,
}) => {
  const [selecting, setSelecting] = useState<string | null>(null);

  async function handleSelect(id: string) {
    if (isLoading || selecting) return;
    setSelecting(id);
    await onSelect(id);
    setSelecting(null);
  }

  return (
    <>
      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <div className="profile-picker-header">
        <div className="profile-picker-icon" aria-hidden="true">👤</div>
        <h2 className="login-title">Scegli il tuo profilo</h2>
        <p className="login-subtitle">
          Questo indirizzo email è associato a {profiles.length} account.
          <br />Seleziona il profilo con cui vuoi accedere.
        </p>
      </div>

      {/* ── Error banner ───────────────────────────────────────────────────── */}
      {error && (
        <div className="error-banner" role="alert">
          <span aria-hidden="true">⚠</span>
          {error}
        </div>
      )}

      {/* ── Profile grid ───────────────────────────────────────────────────── */}
      <div className="profile-grid" role="list">
        {profiles.map((profile, idx) => {
          const isBusy = selecting === profile.id;

          return (
            <button
              key={profile.id}
              className="profile-card"
              role="listitem"
              onClick={() => handleSelect(profile.id)}
              disabled={isLoading || !!selecting}
              aria-label={`Accedi come ${profile.first_name} ${profile.last_name}`}
              style={{ animationDelay: `${idx * CARD_DELAY_MS}ms` }}
            >
              {/* Avatar */}
              <div className="profile-card-avatar" aria-hidden="true">
                {isBusy
                  ? <span className="spinner" style={{ width: 18, height: 18, borderWidth: 2 }} />
                  : initials(profile.first_name, profile.last_name)
                }
              </div>

              {/* Name */}
              <div className="profile-card-name">
                {profile.last_name} {profile.first_name}
              </div>

              {/* Legacy username */}
              <div className="profile-card-username">
                @{profile.username_legacy}
              </div>

              {/* Role badge */}
              <span className="profile-card-badge">
                {roleLabel(profile.role)}
              </span>

              {/* Arrow hint */}
              <span className="profile-card-arrow" aria-hidden="true">›</span>
            </button>
          );
        })}
      </div>

      {/* ── Back ──────────────────────────────────────────────────────────── */}
      <button
        className="back-link"
        onClick={onBack}
        disabled={isLoading || !!selecting}
        type="button"
      >
        ← Torna al login
      </button>
    </>
  );
};
